/*
Not to be confused for comment-macro-extension-handler.tsx in the same package which is used specifically
for the comment editor. This MacroExtensionHandler file is used for creating extension handlers
for next/full-page-editor
*/
import React, { Fragment } from 'react';

import type {
	ExtensionHandler,
	ExtensionHandlers,
} from '@atlaskit/editor-common/extensions/extension-handler';
import type { EditorActions } from '@atlaskit/editor-core/actions';

import { isMigratedMacroImplEnabled } from '@confluence/macro-utils/entry-points/evaluateMacroEnablement.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import {
	getMacroAttributesFromADFNode,
	EDITOR,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import {
	EXTENSION_KEY,
	EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { resolveQuestionsMacroExtensionKey } from '@confluence/fabric-extension-lib/entry-points/macroKeyResolver.ts';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features/entry-points/SubscriptionHandlerFnType.ts';
import { PagePropertiesReportRenderer } from '@confluence/page-properties-report-renderer';
import { DetailsSummaryExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsSummaryExtensionHandler.ts';
import { UserProfileMacroHandler } from '@confluence/profile-macros/entry-points/UserProfileMacroRenderer.ts';
import { ProfilePictureMacroHandler } from '@confluence/profile-macros/entry-points/ProfilePictureMacroRenderer.ts';
import { ContributorsMacroHandler } from '@confluence/contributors-macro/entry-points/ContributorsMacroRenderer.ts';
import { PopularLabelsMacroHandler } from '@confluence/popular-labels-macro/entry-points/PopularLabelsMacroRenderer.ts';
import { ViewPdfMacroHandler } from '@confluence/viewpdf-macro/entry-points/ViewPdfMacroRenderer.ts';
import { WidgetMacroHandler } from '@confluence/widget-macro/entry-points/WidgetMacroRenderer.ts';
import { IFrameMacroHandler } from '@confluence/iframe-macro/entry-points/IFrameMacroRenderer.ts';
import { PageTreeSearchMacroHandler } from '@confluence/pagetree-search-macro/entry-points/PageTreeSearchMacroRenderer.ts';
import { TasksReportMacroHandler } from '@confluence/tasks-report-macro/entry-points/TasksReportMacroRenderer.ts';
import { GlobalQuestionsListMacroHandler } from '@confluence/questions/entry-points/GlobalQuestionsListMacroHandler.ts';
import { LegacyMacroRenderer } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererQuery.ts';
import { RecentlyUpdatedMacroHandler } from '@confluence/recently-updated-macro/entry-points/RecentlyUpdatedMacroRenderer.ts';
import { ContentReportTableMacroHandler } from '@confluence/content-report-table-macro/entry-points/ContentReportTableMacroRenderer.ts';
import { ChangeHistoryMacroHandler } from '@confluence/change-history-macro/entry-points/ChangeHistoryMacroRenderer.ts';

import { AnchorMacroRenderer } from './AnchorMacroRenderer.tsx';
import { DeadMacroRenderer } from './DeadMacroRenderer/index.ts';
import { CreateFromTemplate } from './CreateFromTemplate/index.ts';
import { getMigrationExtHandlerForEditor } from './MigrationExtension/index.ts';
import { ExcerptIncludeExtensionHandler } from './ExcerptInclude/index.ts';
import { AttachmentsMacroHandler } from '@confluence/attachments-macro/entry-points/AttachmentsMacroRenderer.ts';
import { BlogPostsExtensionHandler } from './BlogPosts/BlogPostsExtensionHandler.tsx';
import { ContentByLabelExtensionHandler } from './ContentByLabel/ContentByLabelExtensionHandler.tsx';
import { PageIncludeExtensionHandler } from './PageInclude/PageIncludeExtensionHandler.tsx';
import { TableOfContentsLiveComponent } from './TableOfContentsLiveComponent.tsx';
import { LiveSearchHandler } from './LiveSearch/LiveSearchHandler.tsx';

type Options = {
	contentId: string;
	spaceKey: string;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	editorActions?: EditorActions;
	subscribeDocChange: SubscriptionHandlerFnType;
	unsubscribeDocChange: SubscriptionHandlerFnType;
	isLivePage?: boolean;
	isCollabEditForLiveUpdatesEnabled?: boolean;
};

export const getFullPageExtensionHandlers = ({
	contentId,
	spaceKey,
	extensionContextProvider,
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
	isCollabEditForLiveUpdatesEnabled,
}: Options): ExtensionHandlers => {
	return {
		[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandlerForEditor({
			contentId,
			spaceKey,
			extensionContextProvider,
			editorActions,
			subscribeDocChange,
			unsubscribeDocChange,
			isLivePage,
			isCollabEditForLiveUpdatesEnabled,
		}),
		[EXTENSION_TYPE.MIGRATION]: getMigrationExtHandlerForEditor({
			contentId,
			contextProviders: {}, // we do not yet need ClassicEditorContextProvider for InvalidExtension in next editor
		}),
	};
};

export const getMacroExtensionHandlerForEditor = ({
	contentId,
	spaceKey,
	extensionContextProvider = {
		ClassicEditorContextProvider: ({
			children,
			isWithinEditor: _isWithinEditor,
		}: {
			children: React.ReactNode;
			isWithinEditor?: boolean;
		}) => <>{children}</>,
	},
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
	isCollabEditForLiveUpdatesEnabled,
}): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension, doc) => {
		const resolvedExtensionKey = resolveQuestionsMacroExtensionKey(extension.extensionKey);
		const extensionKey = resolvedExtensionKey;

		// Update extension if key was resolved to a different value
		if (resolvedExtensionKey !== extension.extensionKey) {
			extension = { ...extension, extensionKey: resolvedExtensionKey };
		}

		const attributes = getMacroAttributesFromADFNode(extension);
		const { ClassicEditorContextProvider } = extensionContextProvider;

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			references: doc?.[0]?.references || [],
			contentId,
			spaceKey,
			mode: EDITOR,
			extensionKey,
			attributes,
			multiBodiedExtensionActions: doc?.[0]?.actions,
			isConnectMacro: Boolean(doc?.[0]?.isConnectMacro),
		};

		let implementation;

		const legacyMacroRenderer = (
			<ClassicEditorContextProvider>
				<LegacyMacroRenderer {...macroDefaultProps} />
			</ClassicEditorContextProvider>
		);

		switch (extensionKey) {
			// blocklist - block extensions - we want to show the editor's placeholder frame, so return empty div
			case 'toc': // toc needs to be rewritten as it accesses the DOM to create its tree
				implementation = (
					<TableOfContentsLiveComponent
						macroDefaultProps={macroDefaultProps}
						editorActions={editorActions}
						subscribeDocChange={subscribeDocChange}
						unsubscribeDocChange={unsubscribeDocChange}
						isCollabEditForLiveUpdatesEnabled={!!isCollabEditForLiveUpdatesEnabled}
					/>
				);
				break;

			case 'create-from-template':
				implementation = <CreateFromTemplate {...macroDefaultProps} />;
				break;

			// blocklist - inline extensions - the editor doesn't render a placeholder frame, so we need to display something
			case 'pagetree': // pagetree is not spa friendly and will only work sometimes
				implementation = <DeadMacroRenderer {...macroDefaultProps} />;
				break;

			case 'anchor':
				implementation = <AnchorMacroRenderer {...macroDefaultProps} />;
				break;

			case 'excerpt-include':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<ExcerptIncludeExtensionHandler
							{...macroDefaultProps}
							isLivePage={isLivePage}
							Renderer={PageContentRenderer}
						/>
					</ClassicEditorContextProvider>
				);
				break;

			case 'detailssummary':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						{isMigratedMacroImplEnabled('detailssummary') ? (
							<DetailsSummaryExtensionHandler
								macroDefaultProps={macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								openAnchorsInNewTab
							/>
						) : (
							<PagePropertiesReportRenderer
								macroDefaultProps={macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								openAnchorsInNewTab
							/>
						)}
					</ClassicEditorContextProvider>
				);
				break;
			case EXTENSION_KEY.ATTACHMENTS:
				if (isMigratedMacroImplEnabled(EXTENSION_KEY.ATTACHMENTS)) {
					implementation = (
						<AttachmentsMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
						/>
					);
				} else {
					implementation = legacyMacroRenderer;
				}
				break;
			case 'blog-posts':
				if (isMigratedMacroImplEnabled('blog-posts')) {
					implementation = (
						<ClassicEditorContextProvider isWithinEditor>
							<BlogPostsExtensionHandler
								{...macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								Renderer={PageContentRenderer}
								appearance="full-page"
							/>
						</ClassicEditorContextProvider>
					);
				} else {
					implementation = legacyMacroRenderer;
				}
				break;
			case 'recently-updated':
				if (isMigratedMacroImplEnabled('recently-updated')) {
					implementation = (
						<RecentlyUpdatedMacroHandler
							macroDefaultProps={macroDefaultProps}
							macroParams={{
								...extension?.parameters?.macroParams,
								currentSpaceKey: spaceKey,
							}}
						/>
					);
				} else {
					implementation = legacyMacroRenderer;
				}
				break;
			case 'profile-picture':
				implementation = (
					<ProfilePictureMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'profile':
				implementation = (
					<UserProfileMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'include':
				if (isMigratedMacroImplEnabled('include')) {
					implementation = (
						<ClassicEditorContextProvider isWithinEditor>
							<PageIncludeExtensionHandler
								{...macroDefaultProps}
								macroParams={extension?.parameters?.macroParams}
								Renderer={PageContentRenderer}
								appearance="full-page"
							/>
						</ClassicEditorContextProvider>
					);
				} else {
					implementation = (
						<ClassicEditorContextProvider isWithinEditor>
							<LegacyMacroRenderer {...macroDefaultProps} />
						</ClassicEditorContextProvider>
					);
				}
				break;
			case 'livesearch':
				implementation = <LiveSearchHandler macroDefaultProps={macroDefaultProps} />;
				break;
			case 'questionslist-macro':
				implementation = legacyMacroRenderer;
				break;
			case 'questionslist-macro-native':
				implementation = (
					<GlobalQuestionsListMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'popular-labels':
				implementation = isMigratedMacroImplEnabled('popular-labels') ? (
					<PopularLabelsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'contributors':
				implementation = isMigratedMacroImplEnabled('contributors') ? (
					<ContributorsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'viewpdf':
				implementation = isMigratedMacroImplEnabled('viewpdf') ? (
					<ViewPdfMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'widget':
				implementation = isMigratedMacroImplEnabled('widget') ? (
					<WidgetMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'iframe':
				implementation = isMigratedMacroImplEnabled('iframe') ? (
					<IFrameMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'contentbylabel':
				implementation = isMigratedMacroImplEnabled('contentbylabel') ? (
					<ClassicEditorContextProvider isWithinEditor>
						<ContentByLabelExtensionHandler
							{...macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
							Renderer={PageContentRenderer}
							appearance="full-page"
						/>
					</ClassicEditorContextProvider>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'pagetreesearch':
				implementation = isMigratedMacroImplEnabled('pagetreesearch') ? (
					<PageTreeSearchMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'content-report-table':
				implementation = isMigratedMacroImplEnabled('content-report-table') ? (
					<ContentReportTableMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'tasks-report-macro':
				implementation = isMigratedMacroImplEnabled('tasks-report-macro') ? (
					<TasksReportMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			case 'change-history':
				implementation = isMigratedMacroImplEnabled('change-history') ? (
					<ChangeHistoryMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				) : (
					legacyMacroRenderer
				);
				break;
			// generic macro renderer
			default:
				implementation = legacyMacroRenderer;
		}
		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(EDITOR, extension)}
					contentId={contentId}
					mode={EDITOR}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				{implementation}
			</Fragment>
		);
	});
